import { pathToRegexp } from "path-to-regexp";
export var OPPPermissions;
(function (OPPPermissions) {
    OPPPermissions["FORECAST_READ"] = "forecast.read";
    OPPPermissions["FORECAST_WRITE"] = "forecast.write";
    OPPPermissions["WORKLOAD_ACTIVITIES_READ"] = "workload-activities.read";
    OPPPermissions["WORKLOAD_ACTIVITIES_WRITE"] = "workload-activities.write";
    OPPPermissions["WORKLOAD_BUDGET_READ"] = "workload-budget.read";
    OPPPermissions["WORKLOAD_BUDGET_WRITE"] = "workload-budget.write";
    OPPPermissions["WORKLOAD_FORECAST_READ"] = "workload-forecast.read";
    OPPPermissions["WORKLOAD_HEADCOUNT_READ"] = "workload-headcount.read";
    OPPPermissions["WORKLOAD_HEADCOUNT_WRITE"] = "workload-headcount.write";
    OPPPermissions["WORKLOAD_TASKS_READ"] = "workload-tasks.read";
    OPPPermissions["WORKLOAD_TASKS_WRITE"] = "workload-tasks.write";
})(OPPPermissions || (OPPPermissions = {}));
export var OPPRoles;
(function (OPPRoles) {
    OPPRoles["OPP_DEVELOPER"] = "OPP Developer";
    OPPRoles["OPP_COUNTRY_FUNCTIONAL_MANAGER"] = "OPP Country Functional Manager";
    OPPRoles["OPP_CPP_SPECIALIST"] = "OPP CPP Specialist";
    OPPRoles["OPP_GLOBAL_FUNCTIONAL_MANAGER"] = "OPP Global Functional Manager";
    OPPRoles["OPP_UNIT_FUNCTIONAL_MANAGER"] = "OPP Unit Functional Manager";
    OPPRoles["OPP_UPP_SPECIALIST"] = "OPP UPP Specialist";
    OPPRoles["OPP_LINE_MANAGER"] = "OPP Line Manager";
    OPPRoles["OPP_COUNTRY_TEST_USER"] = "OPP Country Test User";
    OPPRoles["OPP_UNIT_BUSINESS_NAVIGATOR"] = "OPP Unit Business Navigator";
})(OPPRoles || (OPPRoles = {}));
export const userGroupsRole = {
    roles: [
        OPPRoles.OPP_DEVELOPER,
        OPPRoles.OPP_COUNTRY_FUNCTIONAL_MANAGER,
        OPPRoles.OPP_CPP_SPECIALIST,
        OPPRoles.OPP_GLOBAL_FUNCTIONAL_MANAGER,
        OPPRoles.OPP_UNIT_FUNCTIONAL_MANAGER,
        OPPRoles.OPP_UPP_SPECIALIST,
        OPPRoles.OPP_LINE_MANAGER,
        OPPRoles.OPP_COUNTRY_TEST_USER,
        OPPRoles.OPP_UNIT_BUSINESS_NAVIGATOR,
    ],
    permissions: [
        OPPPermissions.FORECAST_READ,
        OPPPermissions.FORECAST_WRITE,
        OPPPermissions.WORKLOAD_ACTIVITIES_READ,
        OPPPermissions.WORKLOAD_BUDGET_READ,
        OPPPermissions.WORKLOAD_ACTIVITIES_WRITE,
        OPPPermissions.WORKLOAD_HEADCOUNT_READ,
        OPPPermissions.WORKLOAD_BUDGET_WRITE,
        OPPPermissions.WORKLOAD_TASKS_READ,
        OPPPermissions.WORKLOAD_FORECAST_READ,
        OPPPermissions.WORKLOAD_TASKS_WRITE,
        OPPPermissions.WORKLOAD_HEADCOUNT_WRITE,
    ],
    orgs: [],
};
export function isPublicEndpoint(path, method, publicEndpoints) {
    const apiURL = new URL(path);
    return publicEndpoints.some((endpoint) => endpoint.path === apiURL.pathname &&
        endpoint.method === method.toLowerCase());
}
export function verifyPermission(path, method, apiGroupList, userInfo) {
    try {
        const apiURL = new URL(path);
        const hasAccess = userInfo?.permissions?.some((accessLevel) => {
            const allowedPaths = apiGroupList.get(accessLevel);
            if (!allowedPaths)
                return false;
            return allowedPaths.some((allowed) => {
                try {
                    const { regexp } = pathToRegexp(allowed.path);
                    const match = regexp.exec(apiURL.pathname);
                    return (!!match && method.toLowerCase() === allowed.method.toLowerCase());
                }
                catch (error) {
                    console.error(`Error matching path "${path}" for access level:`, error);
                    return false;
                }
            });
        });
        return hasAccess;
    }
    catch (error) {
        console.error("Unexpected error in verifyPermission:", error);
        return false;
    }
}
export function isSuperUser(roles) {
    const superUserRoles = [OPPRoles.OPP_DEVELOPER, OPPRoles.OPP_CPP_SPECIALIST];
    return roles?.some((role) => superUserRoles.includes(role));
}
const UNIT_RESTRICTED_ROLES = new Set([
    OPPRoles.OPP_UPP_SPECIALIST,
    OPPRoles.OPP_UNIT_FUNCTIONAL_MANAGER,
    OPPRoles.OPP_UNIT_BUSINESS_NAVIGATOR,
    OPPRoles.OPP_LINE_MANAGER,
]);
// Determines if access to a unit should be restricted based on user role and country
export function shouldRestrictToUnit(userRoles, userCountry, restrictedCountryList) {
    const normalizedCountry = userCountry.trim().toUpperCase();
    if (!normalizedCountry)
        return false;
    const isRestrictedCountry = restrictedCountryList
        .map((c) => c.trim().toUpperCase())
        .filter(Boolean)
        .includes(normalizedCountry);
    if (!isRestrictedCountry)
        return false;
    return (userRoles.length === 1 &&
        UNIT_RESTRICTED_ROLES.has(userRoles[0]));
}
// Every unit-level org in the user's OPP permissions (a user can have several)
export function getUserUnits(permissions) {
    return (permissions.orgs ?? []).flatMap((org) => {
        const { code, type, country } = org.meta;
        if (org.level !== "unit" ||
            code == null ||
            type == null ||
            org.active === false) {
            return [];
        }
        return [{ code, type, country }];
    });
}
// Returns the units the user is limited to in this country when access is
// restricted, otherwise undefined. A restricted user without any unit in the
// country gets an empty list (no access) rather than unrestricted access.
export function getRestrictedUserUnits(permissions, countryCode, restrictedCountryList) {
    if (!shouldRestrictToUnit(permissions.roles, countryCode, restrictedCountryList)) {
        return undefined;
    }
    return getUserUnits(permissions).filter((unit) => unit.country?.toUpperCase() === countryCode.trim().toUpperCase());
}
export function hasUnitAccess(userOrgs, unitCode, unitType, country) {
    if (!userOrgs || userOrgs.length === 0) {
        return false;
    }
    return userOrgs.some((org) => {
        if (org.level !== "unit") {
            return false;
        }
        const meta = org.meta;
        return (meta.code === unitCode &&
            meta.type === unitType &&
            meta.country === country);
    });
}
//# sourceMappingURL=check-accesses.mjs.map